<template>
  <div :data-view-id="componentUniqueTitle || uniqueTitle" class="page-menu-view">
    <ViewTitle :title="title" />
    <nav v-if="leftItems.length || rightItems.length" class="page-menu-nav">
      <b-navbar-nav>
        <MenuEntries
          :items="leftItems"
          :depth="1"
          :application="application"
          :current-page="routePageUniqueTitle"
          :value-text="valueText"
          :children-of="visibleChildren"
          :link-path="linkPath"
          anchor-links
        />
      </b-navbar-nav>
      <b-navbar-nav v-if="rightItems.length" class="ms-auto">
        <MenuEntries
          :items="rightItems"
          :depth="1"
          :application="application"
          :current-page="routePageUniqueTitle"
          :value-text="valueText"
          :children-of="visibleChildren"
          :link-path="linkPath"
          anchor-links
        />
      </b-navbar-nav>
    </nav>
  </div>
</template>

<script>
import axios from 'axios'
import MenuEntries from './MenuEntries.vue'
import ViewTitle from './ViewTitle.vue'
import menuItemsOnSide from '../menu-align.js'
import { pageMenuPath } from '../../../shared/page-menu.js'
import { readLoggedInUser } from '../lib/live-user-storage'

export default {
  name: 'PageMenu',
  components: {
    MenuEntries,
    ViewTitle
  },
  props: {
    uniqueTitle: { type: String, default: '' },
    title: { type: String, default: '' },
    menuItems: { type: Array, default: () => [] },
    componentUniqueTitle: { type: String, default: '' }
  },
  data () {

    return {
      values: {},
      loggedInUser: ''
    }
  },
  computed: {
    application () {

      return (this.$route && this.$route.params && this.$route.params.application) || ''
    },
    visibleItems () {

      return (this.menuItems || []).filter(item => this.itemVisible(item))
    },
    leftItems () {

      return menuItemsOnSide(this.visibleItems, 'left')
    },
    rightItems () {

      return menuItemsOnSide(this.visibleItems, 'right')
    }
  },
  watch: {
    menuItems: {
      deep: true,
      immediate: true,
      handler () {

        this.loadValues()
      }
    }
  },
  mounted () {

    this.loggedInUser = this.readLoggedInUser()
    this._onReloadPage = () => this.loadValues()
    window.addEventListener('superturbo-login', this.onLogin)
    window.addEventListener('superturbo-reload-page', this._onReloadPage)
  },
  beforeUnmount () {

    window.removeEventListener('superturbo-login', this.onLogin)
    window.removeEventListener('superturbo-reload-page', this._onReloadPage)
  },
  methods: {
    readLoggedInUser () {

      try {

        return readLoggedInUser({
          account: this.routeAccount,
          application: this.application
        }) || ''
      } catch (err) {

        return ''
      }
    },
    onLogin (event) {

      this.loggedInUser = (event && event.detail && event.detail.username) || this.readLoggedInUser()
    },
    itemVisible (item) {

      if (!item) return false
      if (item.requiresLogin && !this.loggedInUser) return false
      if (item.hideWhenLoggedIn && this.loggedInUser) return false

      if (item.type === 'dropdown') {

        return this.visibleChildren(item).length > 0
      }

      return true
    },
    visibleChildren (item) {

      if (!item || !item.children) return []

      return item.children.filter(child => {

        if (child.requiresLogin && !this.loggedInUser) return false
        if (child.hideWhenLoggedIn && this.loggedInUser) return false

        return true
      })
    },
    valueText (item) {

      const entry = this.values[item.id]

      if (!entry || entry.display === '' || entry.display == null) return '—'

      return entry.display
    },
    linkPath (item) {

      return {
        path: pageMenuPath({
          application: this.application,
          pageUniqueTitle: item.pageUniqueTitle,
          accessKey: this.routeAccessKey,
          inheritAccessKey: !!item.inheritAccessKey
        })
      }
    },
    async loadValues () {

      const hasValue = (items) => (items || []).some(item => item.type === 'value' || hasValue(item.children))

      if (!hasValue(this.menuItems) || !this.application) {

        this.values = {}

        return
      }

      try {

        const res = await axios.post(`${VUE_APP_PROXY_SERVER_URL}/menu-values/preview`, {
          items: this.menuItems
        }, {
          headers: { 'application-unique-title': this.application }
        })

        this.values = (res.data && res.data.values) || {}
      } catch (err) {

        this.values = {}
      }
    }
  }
}
</script>

<style scoped>
.page-menu-view {
  padding: 0.25rem 0 0.75rem;
}

.page-menu-nav {
  display: flex;
  align-items: center;
  gap: 12px;
  background: transparent;
  padding: 0;
}

.page-menu-nav :deep(.navbar-nav) {
  flex-direction: row;
  gap: 12px;
}

.page-menu-nav :deep(.nav-link) {
  color: inherit;
  padding: 0;
}
</style>
